import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/system";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import { ColDef } from "ag-grid-community";
import { Switch, IconButton, Chip, Paper } from "@mui/material";
import { LockReset } from "@mui/icons-material";
import {
  useUserList,
  useToggleUserStatus,
  useResetUserPassword,
} from "../hooks/useUserList";
import { AgGridReact } from "ag-grid-react";
import { useRecoilState } from "recoil";
import { accountsState } from "../state/accountsState";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const formatDate = (dateString: string) => {
  if (!dateString) return "-";
  const date = dayjs.utc(dateString).local();
  const day = date.format("DD");
  const month = date.format("MM");
  const year = date.format("YYYY");
  const hours = date.format("HH");
  const minutes = date.format("mm");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const AccountsTable = () => {
  const { t } = useTranslation("corporateidp");
  const [accountState, setAccountState] = useRecoilState(accountsState);
  const {
    data: userListData,
    isLoading: initialLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useUserList([
    "userList",
    accountState.filters.email,
    accountState.filters.status,
    accountState.filters.conventionCode,
  ]);

  const toggleUserStatusMutation = useToggleUserStatus();
  const resetUserPasswordMutation = useResetUserPassword();
  // const isLoading = initialLoading && !isError; // Set loading to false when an error occurs

  const columnDefs = useMemo(
    () => [
      { field: "email", headerName: t("list.table.email") },
      { field: "conventionCode", headerName: t("list.table.conventionCode") },
      {
        field: "active",
        headerName: t("list.table.status"),
        cellRenderer: (params: any) => {
          let label;
          let color;
          if (params.value) {
            label = t("list.filters.status.active");
            color = "success";
          } else {
            label = t("list.filters.status.inactive");
            color = "error";
          }
          if (params.data.pending) {
            label = t("list.filters.status.pending");
            color = "warning";
          }

          return (
            <Chip
              label={label}
              sx={{ paddingLeft: "4px", paddingRight: "4px" }}
              color={color as any}
              size="small"
            />
          );
        },
      },
      {
        field: "lastLogin",
        headerName: t("list.table.lastLogin"),
        valueFormatter: (params: any) => formatDate(params.value),
      },
      {
        field: "passwordExpiration",
        headerName: t("list.table.passwordExpiration"),
        valueFormatter: (params: any) => formatDate(params.value),
      },
      {
        field: "_actions",
        headerName: "",
        sortable: false,
        cellRenderer: (params: any) => {
          return (
            <>
              <Switch
                sx={{
                  width: "24px",
                  height: "14px",
                  padding: "0",
                  "& .MuiSwitch-thumb": {
                    width: "10px",
                    height: "10px",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    transform: "translateX(10px)",
                  },
                }}
                onClick={() => {
                  gridRef.current?.api?.showLoadingOverlay();

                  toggleUserStatusMutation.mutate(
                    {
                      userCode: params.data.usercode,
                      status: !params.data.active,
                    },
                    {
                      onSuccess: () => {
                        gridRef.current?.api?.showLoadingOverlay();
                        refetch();
                      },
                      onError: () => {
                        gridRef.current?.api?.showLoadingOverlay();
                        refetch();
                      },
                    }
                  );
                }}
                size="small"
                checked={params.data.active ? true : false}
              />
              <IconButton
                color="accent"
                disabled={params.data.active ? false : true}
                size="small"
                onClick={() => {
                  resetUserPasswordMutation.mutate(params.data.usercode);
                }}
              >
                <LockReset />
              </IconButton>
            </>
          );
        },
      },
    ],
    [t]
  );

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
    };
  }, []);

  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (!isFetching) refetch();
  }, [accountState.filters]);

  useEffect(() => {
    if (isFetching) {
      gridRef.current?.api?.showLoadingOverlay();
    } else gridRef.current?.api?.hideOverlay();
  }, [isFetching]);

  return (
    <Box sx={{ marginTop: "46px", boxShadow: "rgba(0, 160, 223, 0.1)" }}>
      <AgGridCustomTable
        gridRef={gridRef}
        rowData={userListData}
        columnDefs={columnDefs}
        gridOptions={{
          defaultColDef: defaultColDef,
        }}
      />
    </Box>
  );
};
export default AccountsTable;
